import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../contexts/AuthContext';


const DependentsManager = () => {
  const { token, user } = useContext(AuthContext);
  const [dependents, setDependents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [currentDependent, setCurrentDependent] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    relationship: '',
    date_of_birth: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [remainingSlots, setRemainingSlots] = useState(6);

  // Base URL constant
  const BASE_URL = 'https://www.aceswafund.org/welfare/welfare-system';

  // Fetch dependents
  const fetchDependents = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Add cache-busting timestamp
      const timestamp = new Date().getTime();

      const response = await fetch(`${BASE_URL}/get-dependents?user_id=${user.id}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });

      const result = await response.json();

      if (result.status) {
        console.log('Fetched dependents:', result.data);
        setDependents(result.data || []);
        setRemainingSlots(result.remaining_slots || 6 - (result.data?.length || 0));
      } else {
        setError(result.message || 'Failed to fetch dependents');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  }, [token, user?.id]);

  // Fetch dependents on component mount
  useEffect(() => {
    if (user?.id) {
      fetchDependents();
    }
  }, [fetchDependents, user?.id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const openAddModal = () => {
    // Check if max dependents reached
    if (dependents.length >= 6) {
      setError('You cannot have more than 6 dependents. Maximum limit reached.');
      setTimeout(() => setError(null), 3000);
      return;
    }

    setModalMode('add');
    setCurrentDependent(null);
    setFormData({
      name: '',
      relationship: '',
      date_of_birth: ''
    });
    setShowModal(true);
  };

  const openEditModal = (dependent) => {
    setModalMode('edit');
    setCurrentDependent(dependent);
    setFormData({
      name: dependent.name || '',
      relationship: dependent.relationship || '',
      date_of_birth: dependent.date_of_birth || ''
    });
    setShowModal(true);
  };

  // Optimistic update for adding
  const handleAddOptimistic = (newDependent) => {
    setDependents(prev => [...prev, newDependent]);
    setRemainingSlots(prev => Math.max(0, prev - 1));
  };

  // Optimistic update for editing
  const handleEditOptimistic = (updatedDependent) => {
    setDependents(prev => prev.map(dep =>
      dep.id === updatedDependent.id ? updatedDependent : dep
    ));
  };

  // Optimistic update for deleting
  const handleDeleteOptimistic = (id) => {
    setDependents(prev => prev.filter(dep => dep.id !== id));
    setRemainingSlots(prev => Math.min(6, prev + 1));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    // Validate required fields
    if (!formData.name || !formData.relationship) {
      setError('Name and relationship are required');
      setSubmitting(false);
      return;
    }

    // Validate date format if provided
    if (formData.date_of_birth && !/^\d{4}-\d{2}-\d{2}$/.test(formData.date_of_birth)) {
      setError('Invalid date format. Use YYYY-MM-DD');
      setSubmitting(false);
      return;
    }

    // Create optimistic update data
    const optimisticData = {
      ...formData,
      id: modalMode === 'edit' ? currentDependent.id : Date.now(), // Temporary ID for add
      date_of_birth: formData.date_of_birth || null,
      created_at: new Date().toISOString()
    };

    try {
      // Apply optimistic update immediately
      if (modalMode === 'add') {
        handleAddOptimistic(optimisticData);
      } else {
        handleEditOptimistic(optimisticData);
      }

      // Close modal immediately for better UX
      setShowModal(false);
      setSuccess(modalMode === 'add' ? 'Dependent added successfully!' : 'Dependent updated successfully!');
      setTimeout(() => setSuccess(null), 3000);

      // Construct URLs with user_id
      const url = modalMode === 'add'
        ? `${BASE_URL}/add-dependent?user_id=${user.id}`
        : `${BASE_URL}/update-dependent?user_id=${currentDependent.id}`;

      const requestBody = modalMode === 'add'
        ? formData
        : formData;

      console.log('Submitting to:', url);
      console.log('Request body:', requestBody);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      const result = await response.json();
      console.log('Submit result:', result);

      if (result.status) {
        if (modalMode === 'add') {
          // For add mode: Replace the temporary ID with the real ID from server
          setDependents(prev => prev.map(dep =>
            dep.id === optimisticData.id
              ? {
                ...result.data, // Use the complete server response data
                id: result.data.id // Ensure ID is from server
              }
              : dep
          ));
          setRemainingSlots(result.remaining_slots || 0);
        } else {
          // For edit mode: Update with server response data
          setDependents(prev => prev.map(dep =>
            dep.id === currentDependent.id
              ? {
                ...result.data, // Use the complete server response data
                id: currentDependent.id // Keep the same ID
              }
              : dep
          ));
        }
      } else {
        // Revert optimistic update on error
        setError(result.message || 'Operation failed');
        await fetchDependents(); // Refresh to correct state
      }
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('Error:', err);
      // Revert optimistic update on error
      await fetchDependents();
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    setError(null);
    setSubmitting(true);

    // Store the deleted item for potential revert
    const deletedDependent = dependents.find(dep => dep.id === id);

    try {
      // Apply optimistic update immediately
      handleDeleteOptimistic(id);
      setDeleteConfirm(null);
      setSuccess('Dependent deleted successfully!');
      setTimeout(() => setSuccess(null), 3000);

      const response = await fetch(`${BASE_URL}/delete-dependent?user_id=${currentDependent.id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();
      console.log('Delete result:', currentDependent.id);

      if (!result.status) {
        // Revert optimistic update on error
        setError(result.message || 'Delete failed');
        setDependents(prev => [...prev, deletedDependent]);
        setRemainingSlots(prev => Math.max(0, prev - 1));
      } else {
        setRemainingSlots(result.remaining_slots || 0);
      }
      // On success, we keep our optimistic update (the item is already removed)
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('Error:', err);
      // Revert optimistic update on error
      setDependents(prev => [...prev, deletedDependent]);
      setRemainingSlots(prev => Math.max(0, prev - 1));
    } finally {
      setSubmitting(false);
    }
  };

  const getRelationshipIcon = (relationship) => {
    const icons = {
      'Spouse': 'bi-heart-fill',
      'Child': 'bi-person-badge',
      'Parent': 'bi-people-fill',
      'Sibling': 'bi-person-heart',
      'Friend': 'bi-emoji-smile',
      'Other': 'bi-person-circle'
    };
    return icons[relationship] || 'bi-person-circle';
  };

  const getRelationshipColor = (relationship) => {
    const colors = {
      'Spouse': 'danger',
      'Child': 'success',
      'Parent': 'warning',
      'Sibling': 'info',
      'Friend': 'primary',
      'Other': 'secondary'
    };
    return colors[relationship] || 'secondary';
  };

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return null;

    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return age.toString();
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading && dependents.length === 0) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem' }} role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="text-muted">Loading dependents...</p>
      </div>
    );
  }

  return (
    <div className="fade-in">
      {/* Success Toast */}
      {success && (
        <div className="position-fixed top-0 end-0 p-3" style={{ zIndex: 1060 }}>
          <div className="toast show" role="alert">
            <div className="toast-header bg-success text-white">
              <i className="bi bi-check-circle-fill me-2"></i>
              <strong className="me-auto">Success</strong>
              <button type="button" className="btn-close btn-close-white" onClick={() => setSuccess(null)}></button>
            </div>
            <div className="toast-body">
              {success}
            </div>
          </div>
        </div>
      )}

      {/* Error Toast */}
      {error && (
        <div className="position-fixed top-0 end-0 p-3" style={{ zIndex: 1060 }}>
          <div className="toast show" role="alert">
            <div className="toast-header bg-danger text-white">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              <strong className="me-auto">Error</strong>
              <button type="button" className="btn-close btn-close-white" onClick={() => setError(null)}></button>
            </div>
            <div className="toast-body">
              {error}
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="card border-0 shadow-sm rounded-4 mb-4">
        <div className="card-body p-4">
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
            <div>
              <h5 className="fw-bold mb-1">
                <i className="bi bi-people text-primary me-2"></i>
                Dependents
              </h5>
              <p className="text-muted small mb-0">
                {dependents.length > 0
                  ? `You have ${dependents.length} of 6 dependents registered.`
                  : 'Add your dependents. You can add up to 6 dependents.'}
              </p>
            </div>
            <div className="d-flex align-items-center gap-3">
              <div className="text-end">
                <span className="badge bg-primary bg-opacity-10 text-primary rounded-pill px-3 py-2">
                  <i className="bi bi-person-plus me-1"></i>
                  {remainingSlots} slot{remainingSlots !== 1 ? 's' : ''} left
                </span>
              </div>
              {dependents.length < 6 && (
                <button
                  className="btn btn-primary rounded-pill px-4 py-2 d-flex align-items-center justify-content-center gap-2"
                  onClick={openAddModal}
                >
                  <i className="bi bi-plus-lg"></i>
                  <span>Add Dependent</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1055 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 rounded-4">
              <div className="modal-header border-0 pt-4">
                <h5 className="modal-title fw-bold">
                  <i className="bi bi-exclamation-triangle-fill text-warning me-2"></i>
                  Confirm Delete
                </h5>
                <button type="button" className="btn-close" onClick={() => setDeleteConfirm(null)}></button>
              </div>
              <div className="modal-body text-center py-4">
                <div className="bg-danger bg-opacity-10 rounded-circle p-4 d-inline-block mb-3">
                  <i className="bi bi-trash text-danger" style={{ fontSize: '2rem' }}></i>
                </div>
                <p className="mb-2">
                  Are you sure you want to delete <strong className="text-danger">{deleteConfirm.name}</strong>?
                </p>
                <p className="text-muted small mb-0">
                  This action cannot be undone.
                </p>
              </div>
              <div className="modal-footer border-0 pb-4">
                <button
                  type="button"
                  className="btn btn-light rounded-pill px-4"
                  onClick={() => setDeleteConfirm(null)}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger rounded-pill px-4"
                  onClick={() => handleDelete(deleteConfirm.id)}
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Deleting...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-trash me-2"></i>
                      Delete
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 rounded-4">
              <div className="modal-header border-0 pt-4">
                <h5 className="modal-title fw-bold">
                  <i className={`bi ${modalMode === 'add' ? 'bi-plus-circle' : 'bi-pencil'} text-primary me-2`}></i>
                  {modalMode === 'add' ? 'Add Dependent' : 'Edit Dependent'}
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      Full Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-lg"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Enter full name"
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      Relationship <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select form-select-lg"
                      name="relationship"
                      value={formData.relationship}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select relationship</option>
                      <option value="Spouse">Spouse</option>
                      <option value="Child">Child</option>
                      <option value="Parent">Parent</option>
                      <option value="Sibling">Sibling</option>
                      <option value="Friend">Friend</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Date of Birth (Optional)</label>
                    <input
                      type="date"
                      className="form-control form-control-lg"
                      name="date_of_birth"
                      value={formData.date_of_birth}
                      onChange={handleInputChange}
                      placeholder="YYYY-MM-DD"
                    />
                    <small className="text-muted">Format: YYYY-MM-DD</small>
                  </div>
                </div>
                <div className="modal-footer border-0 pb-4">
                  <button
                    type="button"
                    className="btn btn-light rounded-pill px-4"
                    onClick={() => setShowModal(false)}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary rounded-pill px-4"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        {modalMode === 'add' ? 'Adding...' : 'Updating...'}
                      </>
                    ) : (
                      <>
                        <i className={`bi ${modalMode === 'add' ? 'bi-plus-lg' : 'bi-check-lg'} me-2`}></i>
                        {modalMode === 'add' ? 'Add Dependent' : 'Update'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Dependents Grid */}
      {dependents.length === 0 ? (
        <div className="card border-0 shadow-sm rounded-4">
          <div className="card-body text-center py-5">
            <div className="bg-primary bg-opacity-10 rounded-circle p-4 d-inline-block mb-4">
              <i className="bi bi-people text-primary" style={{ fontSize: '3rem' }}></i>
            </div>
            <h4 className="fw-bold mb-3">No Dependents Added</h4>
            <p className="text-muted mb-4">You haven't added any dependents yet. Click the button above to add your first dependent.</p>
          </div>
        </div>
      ) : (
        <div className="row g-4">
          {dependents.map(dependent => {
            const relationshipColor = getRelationshipColor(dependent.relationship);
            const age = dependent.date_of_birth ? calculateAge(dependent.date_of_birth) : null;
            const formattedDob = dependent.date_of_birth ? formatDate(dependent.date_of_birth) : null;

            return (
              <div key={dependent.id} className="col-12 col-md-6 col-lg-4">
                <div className="card border-0 shadow-sm rounded-4 h-100">
                  <div className="card-body p-4">
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <div className={`bg-${relationshipColor} bg-opacity-10 rounded-circle p-3`}>
                        <i className={`bi ${getRelationshipIcon(dependent.relationship)} text-${relationshipColor} fs-3`}></i>
                      </div>
                      <div className="dropdown">
                        <button className="btn btn-link text-dark p-0" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                          <i className="bi bi-three-dots-vertical"></i>
                        </button>
                        <ul className="dropdown-menu dropdown-menu-end">
                          <li>
                            <button
                              className="dropdown-item"
                              onClick={() => openEditModal(dependent)}
                            >
                              <i className="bi bi-pencil me-2"></i>
                              Edit
                            </button>
                          </li>
                          <li>
                            <button
                              className="dropdown-item text-danger"
                              onClick={() => setDeleteConfirm(dependent)}
                            >
                              <i className="bi bi-trash me-2"></i>
                              Delete
                            </button>
                          </li>
                        </ul>
                      </div>
                    </div>

                    <h5 className="fw-bold mb-1">{dependent.name}</h5>
                    <span className={`badge bg-${relationshipColor} bg-opacity-10 text-${relationshipColor} rounded-pill px-3 py-1 mb-3`}>
                      {dependent.relationship}
                    </span>

                    {formattedDob && (
                      <div className="d-flex align-items-center text-muted mb-2">
                        <i className="bi bi-calendar3 me-2"></i>
                        <span>{formattedDob}</span>
                        {age && <span className="ms-2">({age} years)</span>}
                      </div>
                    )}

                    {dependent.created_at && (
                      <div className="d-flex align-items-center text-muted small">
                        <i className="bi bi-clock me-2"></i>
                        <span>Added: {new Date(dependent.created_at).toLocaleDateString()}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {/* Add placeholder cards for remaining slots */}
          {dependents.length < 6 && Array.from({ length: 6 - dependents.length }).map((_, index) => (
            <div key={`placeholder-${index}`} className="col-12 col-md-6 col-lg-4">
              <div className="card border-0 bg-light bg-opacity-50 rounded-4 h-100" style={{ minHeight: '250px' }}>
                <div className="card-body d-flex flex-column align-items-center justify-content-center p-4">
                  <div className="bg-secondary bg-opacity-10 rounded-circle p-3 mb-3">
                    <i className="bi bi-plus-circle text-secondary fs-3"></i>
                  </div>
                  <p className="text-muted text-center mb-0">Empty Slot</p>
                  <small className="text-muted text-center">Add a dependent to fill this slot</small>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <style jsx>{`
        .fade-in {
          animation: fadeIn 0.3s ease-in;
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .form-control-lg, .form-select-lg {
          border-radius: 10px;
          padding: 0.75rem 1rem;
        }
        
        .btn {
          border-radius: 10px;
        }
        
        .rounded-4 {
          border-radius: 1rem !important;
        }
        
        .card {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        
        .card:hover {
          transform: translateY(-5px);
          box-shadow: 0 0.5rem 1rem rgba(0,0,0,0.15) !important;
        }
        
        .badge {
          font-weight: 500;
        }
        
        .modal-content {
          border-radius: 1rem;
        }
        
        .toast {
          min-width: 250px;
        }
        
        .dropdown-menu {
          border-radius: 0.5rem;
          border: none;
          box-shadow: 0 0.5rem 1rem rgba(0,0,0,0.15);
        }
        
        .dropdown-item {
          padding: 0.5rem 1rem;
          cursor: pointer;
        }
        
        .dropdown-item:hover {
          background-color: #f8f9fa;
        }
        
        .dropdown-item.text-danger:hover {
          background-color: #dc3545;
          color: white !important;
        }
        
        @media (max-width: 768px) {
          .btn-lg {
            padding: 0.5rem 1rem;
            font-size: 0.95rem;
          }
          
          .fs-3 {
            font-size: 1.5rem !important;
          }
          
          .p-4 {
            padding: 1rem !important;
          }
          
          .bg-opacity-10.rounded-circle.p-3 {
            padding: 0.75rem !important;
          }
        }
      `}</style>
    </div>
  );
};

export default DependentsManager;